import { useEffect, useState } from "react";

export type CameraOrientation = "portrait" | "landscape" | "landscape-secondary";

function readOrientation(): CameraOrientation {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "portrait";
  if (!window.matchMedia("(orientation: landscape)").matches) return "portrait";
  const type = typeof screen !== "undefined" ? screen.orientation?.type : undefined;
  if (type) return type === "landscape-secondary" ? "landscape-secondary" : "landscape";
  const legacy = (window as unknown as { orientation?: number }).orientation;
  return legacy === -90 ? "landscape-secondary" : "landscape";
}

/**
 * Live viewport orientation for the camera overlay, distinguishing the two
 * landscape directions so the control rail can mirror to the thumb edge. Reads
 * the `(orientation: landscape)` media query plus `screen.orientation.type`,
 * and subscribes to the matchMedia `change`, `screen.orientation` `change`, and
 * window `orientationchange` events. Guards `matchMedia` / `screen.orientation`
 * absence (jsdom, older engines) -> "portrait" and never throws, so every
 * existing camera-capture test stays green. Pure read — never touches the
 * camera stream. See [[burst-camera.spec]] Decision #8.
 */
export function useOrientation(): CameraOrientation {
  const [orientation, setOrientation] = useState<CameraOrientation>(readOrientation);

  useEffect(() => {
    const update = () => setOrientation(readOrientation());

    const mql =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(orientation: landscape)")
        : undefined;
    const so = typeof screen !== "undefined" ? screen.orientation : undefined;

    mql?.addEventListener("change", update);
    so?.addEventListener("change", update);
    window.addEventListener("orientationchange", update);

    // Re-read once on mount in case orientation changed between the initial
    // render and the effect commit (e.g. the overlay opened already landscape).
    update();

    return () => {
      mql?.removeEventListener("change", update);
      so?.removeEventListener("change", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return orientation;
}

/**
 * While `active`, override the manifest portrait lock so the camera overlay may
 * rotate (Android / Chromium installed PWA only), restoring portrait on
 * cleanup. No-op where `screen.orientation.lock` is absent (iOS, jsdom) or
 * rejects (a non-installed browser tab). Never throws and never touches the
 * camera stream — rotation is pure CSS/layout reflow over the same running
 * track (the 2026-06-18 iOS-regression guardrail). See [[burst-camera.spec]]
 * Decision #8 "Orientation lifecycle".
 */
export function useLandscapeUnlock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const so = typeof screen !== "undefined" ? screen.orientation : undefined;
    if (!so || typeof so.lock !== "function") return;
    try {
      // Allow any orientation for the overlay; swallow a rejection (non-installed
      // tab) — the camera simply degrades to portrait, never throws.
      void so.lock("any").catch(() => {});
    } catch {
      // Synchronous throw (e.g. a "Not implemented" engine) — degrade too.
    }
    return () => {
      try {
        so.unlock?.();
      } catch {
        // Unsupported / already unlocked — safe to ignore.
      }
    };
  }, [active]);
}
